import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useController } from '@rest-hooks/react';

import ProductInstanceResource from 'resources/organization/ProductInstanceResource';
import OrganizationResource from 'resources/organization/OrganizationResource';

import DataSelectionTable from 'components/DataTable/DataSelectionTable';

import OrderDetailsModal from './OrderDetailsModal';

import Filter from 'ui/Filter';

import { getFulfillmentColumns } from 'utils/tables/columns/getFulfillmentColumns';
import usePaginationAndFilteringController from 'utils/usePaginationAndFilteringController';

// SELL05/SELL21 — the org's shipping queue: every fulfillable instance,
// carrying the address the org ships to. Status derives from the parcel.
const FulfillmentTabContent = () => {

  const params = useParams();
  const organizationId = parseInt(params.organizationId);

  // The sale-notification email deep-links here with ?query=<order id> so the
  // queue opens pre-filtered to that order's items (SELL25).
  const location = useLocation();
  const initialQuery = new URLSearchParams(location.search).get('query') || '';

  const { fetch } = useController();

  const {
    objectState,
    fetchData,
    handleFilterChange,
    handleQueryChange,
    handlePageChange
  } = usePaginationAndFilteringController({
      fetchDataResource: ProductInstanceResource,
      fetchDataParams: { organization_id: organizationId, fulfillment: true },
      initialQuery,
  });

  useEffect(() => {
    fetchData(1, initialQuery ? { query: initialQuery } : {});
  }, []);

  // Rows band by order so an order's items read as a group; every queue row
  // has an order (SELL21). Parity follows first appearance on the page.
  const orderParity = {};
  let parity = 0;
  for (const row of objectState.filteredObjects) {
    if (!(row.order_id in orderParity)) orderParity[row.order_id] = parity++ % 2;
  }
  const conditionalRowStyles = [
    {
      when: (row) => orderParity[row.order_id] === 1,
      style: { backgroundColor: '#FFFBF9' },
    },
  ];

  // All shipping actions live in the order details modal; the table only
  // opens it.
  const [detailsRow, setDetailsRow] = useState(null);

  // SELL23 — shipping an item also moves the pending count behind the Store
  // nav badge and this tab's label. Both read the organization payload, so
  // refresh that entity too: rest-hooks shares it with the sidebar's own
  // request, and one detail fetch updates both.
  const handleChanged = () => {
    fetchData(objectState.page, objectState.filteringParams);
    fetch(OrganizationResource.detail(), { id: organizationId });
  };

  return (
    <div style={{ flexGrow: 1 }}>
      <DataSelectionTable records={objectState.filteredObjects}
                          columns={getFulfillmentColumns(setDetailsRow)}
                          conditionalRowStyles={conditionalRowStyles}
                          onQueryChange={handleQueryChange}
                          queryString={objectState.query}
                          searchLabel="Search by Order, Buyer or Address"
                          serverPaginationProps={{
                            paginationServer: true,
                            paginationTotalRows: objectState.totalCount,
                            page: objectState.page,
                            onChangePage: handlePageChange
                          }}
                          filter={
                            <Filter
                              label="fulfillment_state"
                              table="product_instances"
                              onChange={handleFilterChange}
                            />
                          }
      />

      {detailsRow && (
        <OrderDetailsModal
          row={detailsRow}
          organizationId={organizationId}
          onChanged={handleChanged}
          onClose={() => setDetailsRow(null)}
        />
      )}
    </div>
  );
};

export default FulfillmentTabContent;
